// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    isWishListLineDeactivated,
    isWishListLineDiscontinued,
    isWishListLineRestricted,
} from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import setWishListLineIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetWishListLineIsSelected";
import updateQuantity from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateQuantity";
import updateUnitOfMeasure from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateUnitOfMeasure";
import updateWishListLineQuantities from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateWishListLineQuantities";
import translate from "@insite/client-framework/Translate";
import { ProductModel, WishListLineModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges, { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import MyListDetailsDiscontinuedProductListLine from "@insite/content-library/Widgets/MyListsDetails/MyListDetailsDiscontinuedProductListLine";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import Clickable from "@insite/mobius/Clickable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import RefreshCw from "@insite/mobius/Icons/RefreshCw";
import { DataTableCell, DataTableRow } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu from "@insite/mobius/OverflowMenu";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@steiner/App/Components/Notify";
import ProductPriceAndActions from "@steiner/App/Components/Product/ProductPriceAndActions";
import { TextFieldPresentationProps } from "@steiner/MobiusOverrides/TextField";
import Edit from "@steiner/XDL/Icons/Edit";
import Plus from "@steiner/XDL/Icons/Plus";
import Trash from "@steiner/XDL/Icons/Trash";
import { getFormattedDate } from "@steiner/XDL/Utilities";
import cloneDeep from "lodash/cloneDeep";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    wishList: WishListModel;
    wishListLine: WishListLineModel;
    productInfo: ProductInfo;
    onDeleteClick: (wishListLine: WishListLineModel) => void;
    onEditNotesClick: (wishListLine: WishListLineModel) => void;
    showImageBadges: boolean;
    maximumNumberOfImageBadges: number;
    showTextBadges: boolean;
    maximumNumberOfTextBadges: number;
    onViewReplacementProduct?: (wishListLine: WishListLineModel) => void;
    // XNG-Changes
    view?: string;
    sortOrderDragComponent?: React.ReactNode;
    editingSortOrder?: boolean;
    updateSortOrder: (wishListLine: WishListLineModel, value: number | string) => void;
    nodeRef?: (node: HTMLElement | null) => void;
    dragStyle?: React.CSSProperties;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    isSelected: !!state.pages.myListDetails.selectedWishListLineIds.find(o => o === ownProps.wishListLine.id),
    settingsCollection: getSettingsCollection(state),
    isQuantityUpdated: state.pages.myListDetails.wishListLinesWithUpdatedQuantity[ownProps.wishListLine.id],
});

const mapDispatchToProps = {
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    updateWishListLineQuantities,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsDetailsProductListLineStyles {
    editNotesLink?: LinkPresentationProps;
    lineInnerContainer?: GridContainerProps;
    selectedCheckboxHidden?: HiddenProps;
    imageGridItem?: GridItemProps;
    selectedCheckbox?: CheckboxPresentationProps;
    imageWrapper?: InjectableCss;
    editNotesHidden?: HiddenProps;
    productImageStyles?: ProductImageStyles;
    descriptionGridItem?: GridItemProps;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    notesStyles?: SmallHeadingAndTextStyles;
    overflowMenuGridItem?: GridItemProps;
    partNumbersHidden?: HiddenProps;
    partNumbersGridItem?: GridItemProps;
    priceAndAvailabilityGridItem?: GridItemProps;
    priceAndAvailabilityInnerContainer?: GridContainerProps;
    purchasedDateGridItem?: GridItemProps;
    purchasedDateLabelText?: TypographyPresentationProps;
    purchasedDateText?: TypographyPresentationProps;
    priceGridItem?: GridItemProps;
    productPriceStyles?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    uomAndQuantityGridItem?: GridItemProps;
    uomAndQuantityInnerContainer?: GridContainerProps;
    uomGridItem?: GridItemProps;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    quantityGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    availabilityGridItem?: GridItemProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    notesHidden?: HiddenProps;
    notesGridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    addToCartButton?: ButtonPresentationProps;
    deleteLinkHidden?: HiddenProps;
    deleteLink?: LinkPresentationProps;
    updateQuantityWrapper?: InjectableCss;
    updateQuantityLink?: LinkPresentationProps;
    quantityUpdatedIcon?: IconPresentationProps;
    quantityUpdatedText?: TypographyPresentationProps;
    restrictedDescriptionGridItem?: GridItemProps;
    restrictedDescriptionText?: TypographyPresentationProps;
    restrictedMessageText?: TypographyPresentationProps;
    restrictedRemoveItemLink?: LinkPresentationProps;
    textBadgeStyles?: ProductTextBadgesStyles;
    imageBadgeStyles?: ProductImageBadgesStyles;
    // XNG-Changes
    descriptionAndBasicInfoItem?: InjectableCss;
    priceAndAvailabilityItem?: InjectableCss;
    productDescriptionDataTableCell?: DataTableCellProps;
    productDateDataTableCell?: DataTableCellProps;
    availabilityDataTableCell?: DataTableCellProps;
    selectAndSort?: InjectableCss;
    tableActionsContainer?: InjectableCss;
    tableUpdateQuantityLinkContainer?: InjectableCss;
    tableQunatityUpdatedTextContainer?: InjectableCss;
    listUpdateQuantityLinkContainer?: InjectableCss;
    listQunatityUpdatedTextContainer?: InjectableCss;
    listActionsContainer?: InjectableCss;
    restrictedLineContainer?: InjectableCss;
    restrictedLineInfo?: InjectableCss;
    restrictedLineActions?: InjectableCss;
    restrictedRemoveItemTableLink?: LinkPresentationProps;
    tableDeleteLink?: LinkPresentationProps;
    sortOrderTextField?: TextFieldPresentationProps;
    actionsWrapper?: InjectableCss;
    DataTableWrapper?: InjectableCss;
}

export const productListLineStyles: MyListsDetailsProductListLineStyles = {
    editNotesLink: {
        typographyProps: { size: 12 },
        css: css`
            margin-top: 5px;
        `,
    },
    lineInnerContainer: {
        gap: 10,
    },
    selectedCheckboxHidden: { below: "md" },
    imageGridItem: {
        width: [3, 3, 2, 2, 1],
        printWidth: 1,
    },
    selectedCheckbox: {
        sizeVariant: "small",
        css: css`
            @media print {
                display: none;
            }
            margin-right: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
            }
            width: 100%;
            min-width: 0;
            flex-direction: column;
        `,
    },
    editNotesHidden: { below: "md" },
    descriptionGridItem: {
        width: [8, 8, 4, 4, 4],
        printWidth: 6,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersStyles: {
        erpNumberGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        erpNumberLabelText: { variant: "legend" },
        customerNameGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        customerNameLabelText: { variant: "legend" },
        manufacturerItemGridItem: {
            css: css`
                flex-direction: column;
            `,
        },
        manufacturerItemLabelText: { variant: "legend" },
    },
    overflowMenuGridItem: {
        width: [1, 1, 0, 0, 0],
        printWidth: 0,
    },
    partNumbersHidden: {
        below: "md",
        css: css`
            @media print {
                display: block;
            }
            width: 100%;
        `,
    },
    partNumbersGridItem: { width: 12 },
    priceAndAvailabilityGridItem: {
        width: [12, 12, 4, 4, 5],
        printWidth: 5,
    },
    priceAndAvailabilityInnerContainer: { gap: 10 },
    purchasedDateGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    purchasedDateLabelText: {
        variant: "p",
        transform: "uppercase",
        weight: 600,
        css: css`
            @media print {
                font-size: 12px;
            }
        `,
    },
    priceGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    productPriceStyles: {
        priceLabelText: {
            variant: "p",
            transform: "uppercase",
            weight: 600,
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: { typographyProps: { size: 12 } },
    },
    uomAndQuantityGridItem: {
        width: [12, 12, 12, 12, 7],
        printWidth: 7,
    },
    uomAndQuantityInnerContainer: {
        gap: 10,
        css: css`
            @media print {
                input,
                select {
                    height: 18px !important;
                    font-size: 11px !important;
                }
            }
        `,
    },
    uomGridItem: { width: 9, printWidth: 6 },
    quantityGridItem: { width: 3, printWidth: 6 },
    productQuantityOrderedStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        cssOverrides: {
            inputSelect: css`
                @media print {
                    width: 100px;
                }
            `,
        },
    },
    productUnitOfMeasureSelectStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        iconProps: {
            css: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
    availabilityGridItem: { width: 12 },
    buttonsGridItem: {
        width: [12, 12, 2, 2, 2],
        printWidth: 0,
        css: css`
            flex-direction: column;
            align-items: center;
        `,
    },
    notesHidden: {
        above: "sm",
        css: css`
            @media print {
                display: none;
            }
            width: 100%;
        `,
    },
    notesGridItem: { width: 12 },
    addToCartButton: {
        css: css`
            width: 100%;
        `,
    },
    deleteLinkHidden: {
        below: "md",
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            align-items: center;
        `,
    },
    deleteLink: {
        typographyProps: { size: 12 },
        css: css`
            margin-top: 10px;
        `,
    },
    updateQuantityWrapper: {
        css: css`
            display: flex;
            margin-top: 10px;
        `,
    },
    updateQuantityLink: {
        typographyProps: { size: 12 },
        icon: {
            iconProps: {
                src: RefreshCw,
                size: 12,
            },
        },
    },
    quantityUpdatedIcon: {
        src: Check,
        size: 16,
        css: css`
            margin-right: 5px;
        `,
    },
    quantityUpdatedText: {
        size: 12,
    },

    textBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
    },
    imageBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
        imageItem: {
            css: css`
                margin: 5px 0;
            `,
        },
    },

    restrictedDescriptionGridItem: {
        width: [12, 12, 6, 6, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    restrictedMessageText: { color: "warning" },
};

const styles = productListLineStyles;

const MyListsDetailsProductListLine: React.FC<Props> = ({
    wishList,
    wishListLine,
    productInfo,
    isSelected,
    settingsCollection,
    isQuantityUpdated,
    onDeleteClick,
    onEditNotesClick,
    updateWishListLineQuantities,
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    showImageBadges,
    showTextBadges,
    maximumNumberOfImageBadges,
    maximumNumberOfTextBadges,
    onViewReplacementProduct,
    // XNG-Changes
    view,
    sortOrderDragComponent,
    editingSortOrder,
    updateSortOrder,
    nodeRef,
    dragStyle,
}) => {
    const product = mapWishListLineToProduct(wishListLine);

    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setWishListLineIsSelected({ wishListLineId: wishListLine.id, isSelected: value });
    };

    const onUpdateQuantityClick = () => {
        updateWishListLineQuantities({ reloadWishListLines: true });
    };

    const canEditWishList = (wishList.allowEdit || !wishList.isSharedList) && !wishList.isAutogenerated;

    const { productSettings } = settingsCollection;
    const productContext: ProductContextModel = {
        product,
        productInfo,
        onQtyOrderedChanged: qtyOrdered => {
            updateQuantity({ wishListLineId: wishListLine.id, qtyOrdered });
        },
        onUnitOfMeasureChanged: unitOfMeasure => {
            updateUnitOfMeasure({ wishListLineId: wishListLine.id, unitOfMeasure });
        },
    };

    // XNG-Changes
    const sortOrderBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        updateSortOrder(wishListLine, event.target.value);
    };

    const sortOrderKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    if (isWishListLineDiscontinued(wishListLine, productInfo) && wishListLine.isActive) {
        return (
            <MyListDetailsDiscontinuedProductListLine
                wishList={wishList}
                wishListLine={wishListLine}
                productContext={productContext}
                isSelected={isSelected}
                selectChangeHandler={selectChangeHandler}
                onDeleteClick={onDeleteClick}
                onViewReplacementProduct={onViewReplacementProduct}
                // XNG-Changes
                {...{ view, sortOrderDragComponent, editingSortOrder, updateSortOrder }}
            />
        );
    }

    if (isWishListLineRestricted(wishListLine, productInfo) || isWishListLineDeactivated(wishListLine)) {
        return view === "list" ? (
            <StyledWrapper
                {...styles.restrictedLineContainer}
                {...{ editingSortOrder }}
                ref={nodeRef}
                style={dragStyle}
            >
                {editingSortOrder && sortOrderDragComponent}
                <StyledWrapper {...styles.restrictedLineInfo} {...{ editingSortOrder }}>
                    <Typography
                        {...styles.restrictedDescriptionText}
                        className="wishlist-restricted-line-description-text"
                    >
                        {wishListLine.shortDescription}
                    </Typography>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.productPartNumbersStyles}
                    />
                    {editingSortOrder && (
                        <TextField
                            {...styles.sortOrderTextField}
                            className="wishlist-line-sort-order-text-field"
                            label={translate("Sort Order")}
                            labelPosition="left"
                            type="number"
                            min={1}
                            defaultValue={wishListLine.sortOrder}
                            onBlur={sortOrderBlurHandler}
                            onKeyPress={sortOrderKeyPressHandler}
                            data-test-selector="myListDetailPage_inputSortOrderLine"
                        />
                    )}
                    <Notify
                        {...styles.restrictedMessageText}
                        className="wishlist-restricted-line-message-text"
                        data-test-selector="restrictedMessage"
                        type="warning"
                    >
                        {isWishListLineRestricted(wishListLine, productInfo) &&
                            !isWishListLineDeactivated(wishListLine) && (
                                <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Restrictions")}</>
                            )}
                        {isWishListLineDeactivated(wishListLine) && (
                            <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Deactivation")}</>
                        )}
                    </Notify>
                </StyledWrapper>
                <StyledWrapper {...styles.restrictedLineActions} {...{ editingSortOrder }}>
                    {canEditWishList && (
                        <Link
                            className="wishlist-restricted-line-remove-link"
                            {...styles.restrictedRemoveItemLink}
                            onClick={() => onDeleteClick(wishListLine)}
                            data-test-selector="removeRestricted"
                        >
                            {translate("Delete Item")}
                        </Link>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        ) : (
            <DataTableRow>
                <DataTableCell></DataTableCell>
                <DataTableCell {...styles.productDescriptionDataTableCell}>
                    <Typography
                        {...styles.restrictedDescriptionText}
                        className="wishlist-restricted-line-description-text"
                    >
                        {wishListLine.shortDescription}
                    </Typography>
                </DataTableCell>
                <DataTableCell></DataTableCell>
                <DataTableCell>{wishListLine.manufacturerItem}</DataTableCell>
                <DataTableCell>{product.customerProductNumber || product.productNumber}</DataTableCell>
                <DataTableCell {...styles.availabilityDataTableCell} colSpan={2}>
                    <Notify
                        {...styles.restrictedMessageText}
                        className="wishlist-restricted-line-message-text"
                        data-test-selector="restrictedMessage"
                        type="warning"
                    >
                        {isWishListLineRestricted(wishListLine, productInfo) &&
                            !isWishListLineDeactivated(wishListLine) && (
                                <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Restrictions")}</>
                            )}
                        {isWishListLineDeactivated(wishListLine) && (
                            <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Deactivation")}</>
                        )}
                    </Notify>
                </DataTableCell>
                <DataTableCell>
                    {!!editingSortOrder && (
                        <TextField
                            {...styles.sortOrderTextField}
                            className="wishlist-line-sort-order-text-field"
                            type="number"
                            min={1}
                            defaultValue={wishListLine.sortOrder}
                            onBlur={sortOrderBlurHandler}
                            onKeyPress={sortOrderKeyPressHandler}
                            data-test-selector="myListDetailPage_inputSortOrderLine"
                        />
                    )}
                </DataTableCell>
                <DataTableCell>
                    {canEditWishList && (
                        <Link
                            className="wishlist-restricted-line-remove-link"
                            {...styles.restrictedRemoveItemTableLink}
                            onClick={() => onDeleteClick(wishListLine)}
                            data-test-selector="removeRestricted"
                        ></Link>
                    )}
                </DataTableCell>
            </DataTableRow>
        );
        // return (
        //     <GridContainer
        //         {...styles.lineInnerContainer}
        //         className="wishlist-restricted-line-grid-container"
        //         data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
        //     >
        //         <GridItem
        //             {...styles.restrictedDescriptionGridItem}
        //             className="wishlist-restricted-line-description-grid-item"
        //         >
        //             <Typography
        //                 {...styles.restrictedDescriptionText}
        //                 className="wishlist-restricted-line-description-text"
        //             >
        //                 {wishListLine.shortDescription}
        //             </Typography>
        //         </GridItem>
        //         <GridItem {...styles.partNumbersGridItem} className="wishlist-restricted-line-part-number-grid-item">
        //             <ProductPartNumbers
        //                 productNumber={product.productNumber}
        //                 customerProductNumber={product.customerProductNumber}
        //                 manufacturerItem={product.manufacturerItem}
        //                 extendedStyles={styles.productPartNumbersStyles}
        //             />
        //         </GridItem>
        //         <GridItem {...styles.priceAndAvailabilityGridItem}>
        //             <Typography
        //                 {...styles.restrictedMessageText}
        //                 className="wishlist-restricted-line-message-text"
        //                 data-test-selector="restrictedMessage"
        //             >
        //                 {isWishListLineRestricted(wishListLine, productInfo) &&
        //                     !isWishListLineDeactivated(wishListLine) && (
        //                         <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Restrictions")}</>
        //                     )}
        //                 {isWishListLineDeactivated(wishListLine) && (
        //                     <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Deactivation")}</>
        //                 )}
        //             </Typography>
        //         </GridItem>
        //         <GridItem {...styles.buttonsGridItem} className="wishlist-restricted-line-buttons-grid-item">
        //             {canEditWishList && (
        //                 <Link
        //                     className="wishlist-restricted-line-remove-link"
        //                     {...styles.restrictedRemoveItemLink}
        //                     onClick={() => onDeleteClick(wishListLine)}
        //                     data-test-selector="removeRestricted"
        //                 >
        //                     {translate("Remove item")}
        //                 </Link>
        //             )}
        //         </GridItem>
        //     </GridContainer>
        // );
    }

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    // XNG-Changes
    const selectLineCheckbox = (
        <Checkbox
            {...styles.selectedCheckbox}
            checked={isSelected}
            onChange={selectChangeHandler}
            data-test-selector="myListDetails_selectRowCheckbox"
        />
    );

    const priceAndAvailabilityContainer = (
        <StyledWrapper {...styles.priceAndAvailabilityItem}>
            <GridContainer
                {...styles.priceAndAvailabilityInnerContainer}
                className="wishlist-line-price-availability-grid-container"
            >
                {wishList.isAutogenerated && (
                    <GridItem {...styles.purchasedDateGridItem} className="wishlist-line-purchased-date-grid-container">
                        <Typography
                            {...styles.purchasedDateLabelText}
                            className="wishlist-line-purchased-date-label-text"
                        >
                            {translate("Purchased Date")}
                        </Typography>
                        <Typography
                            {...styles.purchasedDateText}
                            className="wishlist-line-purchased-date-text"
                            data-test-selector="purchasedDate"
                        >
                            <LocalizedDateTime dateTime={wishListLine.createdOn} />
                        </Typography>
                    </GridItem>
                )}
                <GridItem {...styles.priceGridItem} className="wishlist-line-price-grid-container">
                    <ProductPrice
                        product={productContext}
                        extendedStyles={styles.productPriceStyles}
                        showLabel={false}
                        showSalePriceLabel={false}
                    />
                    <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                </GridItem>
                {productSettings.showInventoryAvailability && (
                    <GridItem {...styles.availabilityGridItem} className="wishlist-line-availability-grid-item">
                        <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                    </GridItem>
                )}
            </GridContainer>
        </StyledWrapper>
    );

    const deleteLink = (
        <Link
            className="wishlist-line-delete-link"
            onClick={() => onDeleteClick(wishListLine)}
            data-test-selector="deleteListItem"
            {...(view === "list" ? styles.deleteLink : styles.tableDeleteLink)}
        >
            {view === "list" ? translate("Delete Item") : ""}
        </Link>
    );

    const updateQuantityLink = (
        <Link
            {...styles.updateQuantityLink}
            className="wishlist-line-update-quantity-link"
            onClick={() => onUpdateQuantityClick()}
        >
            {translate("Update QTY")}
        </Link>
    );

    const quantityUpdatedText = (
        <StyledWrapper>
            <Icon {...styles.quantityUpdatedIcon} className="wishlist-line-quantity-updated-icon" />
            <Typography {...styles.quantityUpdatedText} className="wishlist-line-quantity-updated-text">
                {translate("QTY updated")}
            </Typography>
        </StyledWrapper>
    );

    return (
        <ProductContext.Provider value={productContext}>
            {/* XNG-Changes: Restructuring of the product line to include list and table views */}
            {view === "list" ? (
                <StyledWrapper
                    {...styles.lineInnerContainer}
                    className="wishlist-line-grid-container"
                    data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
                    ref={nodeRef}
                    style={dragStyle}
                >
                    <StyledWrapper {...styles.selectAndSort}>
                        {selectLineCheckbox}
                        {editingSortOrder && sortOrderDragComponent}
                    </StyledWrapper>
                    <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                    <StyledWrapper {...styles.descriptionAndBasicInfoItem}>
                        {/*
                            Hide Brand Name
                            https://xngage.atlassian.net/browse/STNRS-873
                        */}
                        {/* {product.brand && (
                            <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                        )} */}
                        <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            extendedStyles={styles.productPartNumbersStyles}
                        />
                        {wishListLine.notes && (
                            <SmallHeadingAndText
                                heading={translate("Line Note")}
                                text={wishListLine.notes}
                                extendedStyles={styles.notesStyles}
                            />
                        )}
                        {canEditWishList && (
                            <Link
                                {...styles.editNotesLink}
                                className="wishlist-line-edit-notes-link"
                                onClick={() => onEditNotesClick(wishListLine)}
                                data-test-selector="myListDetails_addOrEditNote"
                                icon={{
                                    iconProps: {
                                        src: wishListLine.notes ? Edit : Plus,
                                        size: 12,
                                    },
                                }}
                            >
                                {translate(`${wishListLine.notes ? "Edit" : "Add"} Line Note`)}
                            </Link>
                        )}
                        {showTextBadges && textBadges && (
                            <ProductTextBadges
                                badgeList={textBadges}
                                maxBadges={maximumNumberOfTextBadges ?? 4}
                                extendedStyles={styles.textBadgeStyles}
                            />
                        )}
                        {showImageBadges && imageBadges && (
                            <ProductImageBadges
                                badgeList={imageBadges}
                                maxBadges={maximumNumberOfImageBadges ?? 4}
                                extendedStyles={styles.imageBadgeStyles}
                                numberOfColumns={4}
                            />
                        )}
                        {editingSortOrder && (
                            <TextField
                                {...styles.sortOrderTextField}
                                className="wishlist-line-sort-order-text-field"
                                label={translate("Sort Order")}
                                labelPosition="left"
                                type="number"
                                min={1}
                                defaultValue={wishListLine.sortOrder}
                                onBlur={sortOrderBlurHandler}
                                onKeyPress={sortOrderKeyPressHandler}
                                data-test-selector="myListDetailPage_inputSortOrderLine"
                            />
                        )}
                    </StyledWrapper>
                    {priceAndAvailabilityContainer}
                    <StyledWrapper {...styles.listActionsContainer}>
                        <ProductQuantityOrdered extendedStyles={styles.productQuantityOrderedStyles} />
                        <ProductAddToCartButton
                            notes={wishListLine.notes}
                            extendedStyles={styles.addToCartButton}
                            data-test-selector="addToCart"
                        />
                        {canEditWishList && (
                            <StyledWrapper {...styles.actionsWrapper}>
                                <>
                                    {productInfo.qtyOrdered > 0 &&
                                        wishListLine.qtyOrdered !== productInfo.qtyOrdered && (
                                            <StyledWrapper {...styles.listUpdateQuantityLinkContainer}>
                                                {updateQuantityLink}
                                            </StyledWrapper>
                                        )}

                                    {isQuantityUpdated && wishListLine.qtyOrdered === productInfo.qtyOrdered && (
                                        <StyledWrapper {...styles.listQunatityUpdatedTextContainer}>
                                            {quantityUpdatedText}
                                        </StyledWrapper>
                                    )}
                                </>
                                {deleteLink}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            ) : (
                <DataTableRow {...styles.DataTableWrapper}>
                    <DataTableCell>{selectLineCheckbox}</DataTableCell>
                    <DataTableCell {...styles.productDescriptionDataTableCell}>
                        <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
                    </DataTableCell>
                    <DataTableCell {...styles.productDateDataTableCell}>
                        {!!wishListLine.createdOn && getFormattedDate(new Date(wishListLine.createdOn))}
                    </DataTableCell>
                    <DataTableCell {...styles.productDateDataTableCell}>{wishListLine.manufacturerItem}</DataTableCell>
                    <DataTableCell {...styles.productDateDataTableCell}>
                        {product.customerProductNumber || product.productNumber}
                    </DataTableCell>
                    <DataTableCell {...styles.availabilityDataTableCell}>
                        <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                    </DataTableCell>
                    <DataTableCell>
                        <ProductPrice
                            product={productContext}
                            extendedStyles={styles.productPriceStyles}
                            showLabel={false}
                            showSalePriceLabel={false}
                        />
                    </DataTableCell>
                    <DataTableCell>
                        {editingSortOrder ? (
                            <TextField
                                {...styles.sortOrderTextField}
                                className="wishlist-line-sort-order-text-field"
                                type="number"
                                min={1}
                                defaultValue={wishListLine.sortOrder}
                                onBlur={sortOrderBlurHandler}
                                onKeyPress={sortOrderKeyPressHandler}
                                data-test-selector="myListDetailPage_inputSortOrderLine"
                            />
                        ) : (
                            <StyledWrapper {...styles.tableActionsContainer}>
                                <ProductQuantityOrdered extendedStyles={styles.productQuantityOrderedStyles} />
                                <ProductAddToCartButton
                                    notes={wishListLine.notes}
                                    extendedStyles={styles.addToCartButton}
                                    data-test-selector="addToCart"
                                    {...{ iconOnly: true }}
                                />
                                {canEditWishList && (
                                    <>
                                        {productInfo.qtyOrdered > 0 &&
                                            wishListLine.qtyOrdered !== productInfo.qtyOrdered && (
                                                <StyledWrapper {...styles.tableUpdateQuantityLinkContainer}>
                                                    {updateQuantityLink}
                                                </StyledWrapper>
                                            )}

                                        {isQuantityUpdated && wishListLine.qtyOrdered === productInfo.qtyOrdered && (
                                            <StyledWrapper {...styles.tableQunatityUpdatedTextContainer}>
                                                {quantityUpdatedText}
                                            </StyledWrapper>
                                        )}
                                    </>
                                )}
                            </StyledWrapper>
                        )}
                    </DataTableCell>
                    <DataTableCell>{canEditWishList && deleteLink}</DataTableCell>
                </DataTableRow>
            )}
            {/* <GridContainer
                {...styles.lineInnerContainer}
                className="wishlist-line-grid-container"
                data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
            >
                <GridItem {...styles.imageGridItem}>
                    <Hidden {...styles.selectedCheckboxHidden}>
                        <Checkbox
                            {...styles.selectedCheckbox}
                            checked={isSelected}
                            onChange={selectChangeHandler}
                            hiddenIconText={translate("Select")}
                            data-test-selector="myListDetails_selectRowCheckbox"
                        />
                    </Hidden>
                    <StyledWrapper {...styles.imageWrapper} className="wishlist-line-product-image-wrapper">
                        <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                        {canEditWishList && (
                            <Hidden {...styles.editNotesHidden}>
                                <Link
                                    {...styles.editNotesLink}
                                    className="wishlist-line-edit-notes-link"
                                    onClick={() => onEditNotesClick(wishListLine)}
                                    data-test-selector="myListDetails_addOrEditNote"
                                >
                                    {translate(`${wishListLine.notes ? "Edit" : "Add"} Notes`)}
                                </Link>
                            </Hidden>
                        )}
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.descriptionGridItem} className="wishlist-line-description-grid-item">
                    {product.brand && <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />}
                    <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
                    <Hidden {...styles.partNumbersHidden}>
                        <GridItem {...styles.partNumbersGridItem}>
                            <ProductPartNumbers
                                productNumber={product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                extendedStyles={styles.productPartNumbersStyles}
                            />
                        </GridItem>
                        {wishListLine.notes && (
                            <GridItem {...styles.notesGridItem} className="wishlist-line-notes-grid-item">
                                <SmallHeadingAndText
                                    heading={translate("Notes")}
                                    text={wishListLine.notes}
                                    extendedStyles={styles.notesStyles}
                                />
                            </GridItem>
                        )}
                    </Hidden>

                    {showTextBadges && textBadges && (
                        <ProductTextBadges
                            badgeList={textBadges}
                            maxBadges={maximumNumberOfTextBadges ?? 4}
                            extendedStyles={styles.textBadgeStyles}
                        />
                    )}
                    {showImageBadges && imageBadges && (
                        <ProductImageBadges
                            badgeList={imageBadges}
                            maxBadges={maximumNumberOfImageBadges ?? 4}
                            extendedStyles={styles.imageBadgeStyles}
                            numberOfColumns={4}
                        />
                    )}
                </GridItem>
                {canEditWishList && (
                    <GridItem {...styles.overflowMenuGridItem}>
                        <OverflowMenu position="end">
                            <Clickable onClick={() => onEditNotesClick(wishListLine)}>
                                {translate(`${wishListLine.notes ? "Edit" : "Add"} Notes`)}
                            </Clickable>
                            <Clickable onClick={() => onDeleteClick(wishListLine)}>{translate("Delete")}</Clickable>
                        </OverflowMenu>
                    </GridItem>
                )}
                <Hidden {...styles.notesHidden}>
                    <GridItem {...styles.partNumbersGridItem} className="wishlist-line-product-part-number-grid-item">
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            extendedStyles={styles.productPartNumbersStyles}
                        />
                    </GridItem>
                </Hidden>
                <GridItem {...styles.priceAndAvailabilityGridItem}>
                    <GridContainer
                        {...styles.priceAndAvailabilityInnerContainer}
                        className="wishlist-line-price-availability-grid-container"
                    >
                        {wishList.isAutogenerated && (
                            <GridItem
                                {...styles.purchasedDateGridItem}
                                className="wishlist-line-purchased-date-grid-container"
                            >
                                <Typography
                                    {...styles.purchasedDateLabelText}
                                    className="wishlist-line-purchased-date-label-text"
                                >
                                    {translate("Purchased Date")}
                                </Typography>
                                <Typography
                                    {...styles.purchasedDateText}
                                    className="wishlist-line-purchased-date-text"
                                    data-test-selector="purchasedDate"
                                >
                                    <LocalizedDateTime dateTime={wishListLine.createdOn} />
                                </Typography>
                            </GridItem>
                        )}
                        <GridItem {...styles.priceGridItem} className="wishlist-line-price-grid-container">
                            <ProductPrice
                                product={productContext}
                                showSavings={true}
                                showSavingsAmount={productSettings.showSavingsAmount}
                                showSavingsPercent={productSettings.showSavingsPercent}
                                extendedStyles={styles.productPriceStyles}
                            />
                            <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                        </GridItem>
                        <GridItem {...styles.uomAndQuantityGridItem}>
                            <GridContainer
                                {...styles.uomAndQuantityInnerContainer}
                                className="wishlist-line-uom-quantity-grid-container"
                            >
                                {product.unitOfMeasures && product.unitOfMeasures.length > 1 && (
                                    <GridItem {...styles.uomGridItem}>
                                        <ProductUnitOfMeasureSelect
                                            disabled={!canEditWishList}
                                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                        />
                                    </GridItem>
                                )}
                                <GridItem {...styles.quantityGridItem} className="wishlist-line-quantity-grid-item">
                                    <ProductQuantityOrdered extendedStyles={styles.productQuantityOrderedStyles} />
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        {productSettings.showInventoryAvailability && (
                            <GridItem {...styles.availabilityGridItem} className="wishlist-line-availability-grid-item">
                                <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
                {wishListLine.notes && (
                    <Hidden {...styles.notesHidden}>
                        <GridItem {...styles.notesGridItem} className="wishlist-line-notes-grid-item">
                            <SmallHeadingAndText
                                heading={translate("Notes")}
                                text={wishListLine.notes}
                                extendedStyles={styles.notesStyles}
                            />
                        </GridItem>
                    </Hidden>
                )}
                <GridItem {...styles.buttonsGridItem} className="wishlist-line-buttons-grid-item">
                    <ProductAddToCartButton
                        notes={wishListLine.notes}
                        extendedStyles={styles.addToCartButton}
                        data-test-selector="addToCart"
                    />
                    {canEditWishList && (
                        <>
                            <Hidden {...styles.deleteLinkHidden}>
                                <Link
                                    {...styles.deleteLink}
                                    className="wishlist-line-delete-link"
                                    onClick={() => onDeleteClick(wishListLine)}
                                    data-test-selector="deleteListItem"
                                >
                                    {translate("Delete")}
                                </Link>
                            </Hidden>
                            <StyledWrapper
                                {...styles.updateQuantityWrapper}
                                className="wishlist-line-update-quantity-wrapper"
                            >
                                {productInfo.qtyOrdered > 0 && wishListLine.qtyOrdered !== productInfo.qtyOrdered && (
                                    <Link
                                        {...styles.updateQuantityLink}
                                        className="wishlist-line-update-quantity-link"
                                        onClick={() => onUpdateQuantityClick()}
                                    >
                                        {translate("Update QTY")}
                                    </Link>
                                )}
                                {isQuantityUpdated && wishListLine.qtyOrdered === productInfo.qtyOrdered && (
                                    <>
                                        <Icon
                                            {...styles.quantityUpdatedIcon}
                                            className="wishlist-line-quantity-updated-icon"
                                        />
                                        <Typography
                                            {...styles.quantityUpdatedText}
                                            className="wishlist-line-quantity-updated-text"
                                        >
                                            {translate("QTY updated")}
                                        </Typography>
                                    </>
                                )}
                            </StyledWrapper>
                        </>
                    )}
                </GridItem>
            </GridContainer> */}
        </ProductContext.Provider>
    );
};

const mapWishListLineToProduct = (wishListLine: WishListLineModel) => {
    const product = cloneDeep(wishListLine) as any as ProductModel;
    product.id = wishListLine.productId;
    product.productNumber = wishListLine.erpNumber;
    product.productTitle = wishListLine.shortDescription;
    product.customerProductNumber = wishListLine?.customerName;
    product.unitOfMeasures =
        wishListLine?.productUnitOfMeasures?.map(u => ({
            id: u.productUnitOfMeasureId,
            ...u,
        })) || null;

    return product;
};

export default connect(mapStateToProps, mapDispatchToProps)(MyListsDetailsProductListLine);
